import React, { forwardRef, useState } from 'react';

import Input, { InputProps } from './Input';

import Icons from '@/components/icons';

const formatNumberToDisplayValue = (
  numericValue: number | undefined,
  withDecimals: boolean,
  maxDigits: number
): string => {
  if (numericValue === undefined || Number.isNaN(numericValue)) return '';

  const valueStr = numericValue.toString();

  const parts = valueStr.split('.');
  let integerPart = parts[0].slice(0, maxDigits);
  const decimalPart = parts.length > 1 ? parts[1].slice(0, 2) : '';

  integerPart = integerPart.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ".");

  if (withDecimals && decimalPart.length > 0) {
    // Only show the decimals as they are, do not pad with zeros
    return `${integerPart},${decimalPart}`;
  }

  return integerPart;
};

const parseDisplayValueToNumber = (displayValue: string): number | undefined => {
  if (!displayValue) return undefined;

  const numericString = displayValue.replaceAll('.', '').replaceAll(',', '.');
  return numericString ? Number(numericString) : 0;
};

const formatUserInput = (valueStr: string, withDecimals: boolean, maxDigits: number): string => {
  if (!withDecimals) {
    valueStr = valueStr.replaceAll(",", "");
  }

  let val = valueStr.replaceAll(/[^0-9,]/g, "");

  const firstCommaIndex = val.indexOf(",");
  let beforeComma: string;
  let afterComma: string;

  if (firstCommaIndex !== -1 && withDecimals) {
    beforeComma = val.substring(0, firstCommaIndex).slice(0, maxDigits);
    afterComma = val.substring(firstCommaIndex + 1).replaceAll(",", "").slice(0, 2);

    beforeComma = beforeComma.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ".");

    val = `${beforeComma},${afterComma}`;
  } else {
    beforeComma = val.slice(0, maxDigits);

    beforeComma = beforeComma.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ".");

    val = beforeComma;
  }

  return val;
};

interface CurrencyInputProps extends Omit<InputProps, 'prefix' | 'type' | 'postfix' | 'onChange'> {
  inputStyle?: 'table';
  maxDigits?: number;
  onChange?: (value: number | undefined) => void;
  value?: number;
  defaultValue?: number;
  withDecimals?: boolean;
}

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  function CurrencyInput(
    {
      defaultValue,
      value,
      maxDigits = 7,
      onChange,
      withDecimals = false,
      ...props
    },
    _ref
  ) {
    const [displayValue, setDisplayValue] = useState<string>(() => {
      const valueToFormat = value !== undefined ? value : defaultValue;
      return formatNumberToDisplayValue(valueToFormat, withDecimals, maxDigits);
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formattedDisplayValue = formatUserInput(e.target.value, withDecimals, maxDigits);
      setDisplayValue(formattedDisplayValue);

      const numericValue = parseDisplayValueToNumber(formattedDisplayValue);

      if (onChange) {
        onChange(numericValue);
      }
    };

    return (
      <Input
        {...props}
        onChange={handleChange}
        prefix={<Icons.EuroIcon fontSize={22} />}
        type="text"
        value={displayValue}
      />
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';

export default CurrencyInput;
